import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  model,
  output,
  resource,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

import { SvgIconComponent } from 'angular-svg-icon';
import { SOCKET_TIMEOUT_INVENTORY_SAFETY_MS } from '@core/constants/socket-timeouts.constants';
import { SearchInputComponent } from '../../../core/components/search-input/search-input.component';
import { SessionService } from '@core/services/session.service';
import { CostComponent } from '../../../core/components/cost/cost.component';
import { Skin } from '@core/interfaces/skin.interface';
import { DepositService } from '@features/deposit/services/deposit.service';
import { SocketService } from '@core/services/socket.service';
import {
  SkinsListComponent,
  SkinsListConfig,
} from '../../../core/components/skins-list/skins-list.component';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { sleep } from '@core/utils/sleep.util';
import { DepositSkin } from '@features/deposit/interfaces/deposit-skin.interface';
import { CurrencyPipe } from '@angular/common';
import { WithdrawSkin } from '@features/withdraw/interfaces/withdraw-skin.interface';
import { BalanceType } from '@core/types/balance.type';
import { LoadingIconComponent } from '../loading-icon/loading-icon.component';
import { normalizeSkinImage } from '@core/utils/skins.util';
import { ToggleComponent } from '../toggle/toggle.component';

enum SortOptions {
  highest_price,
  lowest_price,
}

interface SortOption {
  label: string;
  key: keyof typeof SortOptions;
  fn: () => void;
}

export interface InventoryOptions {
  items_load_endpoint: string;
  items_load_endpoint_options?: Record<string, string | number>;
  items_search_enabled: boolean;
  items_sort_enabled: boolean;
  items_pure_filter_enabled: boolean;
  //
  typography_title?: string;
  typography_title_icon?: string;
  typography_action_button?: string;

  tabs?: {
    items_skins_text: string;
    items_pure_text: string;
    items_pure_icon: string;
    items_skins_icon: string;
  };
}

interface InventoryPureOptions {
  items_pure_load_endpoint: string;
  items_pure_load_endpoint_options?: Record<string, string | number>;
}

interface InventoryRequirementsOptions {
  requirements_title: string;
  requirements_accent_word: string;
  requirements_options_min_value: number;
  requirements_options_max_value: number;
  requirements_options_min_items: number;
  requirements_options_max_items: number;
}

interface InventoryHeaderUISettings {
  gap?: number;
  search_order?: number;
  pure_order?: number;
  sort_order?: number;
  slot_order?: number;
}

interface InventoryFooterUISettings {
  disabled?: boolean;
  hide_requirements_info?: boolean;
  show_refresh_button?: boolean;
}

export type CommonSkin = DepositSkin & WithdrawSkin;

@Component({
  selector: 'app-inventory-template',
  imports: [
    SearchInputComponent,
    SvgIconComponent,
    CostComponent,
    SkinsListComponent,
    CheckboxComponent,
    CurrencyPipe,
    LoadingIconComponent,
    ToggleComponent,
  ],

  templateUrl: './inventory-template.component.html',
  styleUrl: './inventory-template.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  host: {
    class: 'modal-container flex flex-col',
    '[class.theme--withdraw-store]': 'theme() === "withdraw-store"',
    '[class.fixed-columns]': 'inventorySkinsListConfig()?.columns',
    '[class.fixed-columns-responsive]':
      'inventorySkinsListConfig()?.columns && inventorySkinsListConfig()?.responsive_columns',
    '[style.--skin-min-width]': 'inventorySkinsListConfig()?.skin_min_width',
    '[style.--skin-columns]': 'inventorySkinsListConfig()?.columns',
    '[style.--mobile-skin-min-width]':
      'inventorySkinsListConfig()?.mobile?.skin_min_width',
    '[style.--mobile-padding]': 'inventorySkinsListConfig()?.mobile?.padding',
  },
})
export class InventoryTemplateComponent {
  balanceType = input<BalanceType | null | undefined>(null);
  inventoryOptions = input.required<InventoryOptions>();
  inventoryPureOptions = input<InventoryPureOptions | null>(null);
  requirementsOptions = input<InventoryRequirementsOptions>();
  inventoryHeaderUISettings = input<InventoryHeaderUISettings>();
  inventoryFooterUISettings = input<InventoryFooterUISettings>();
  inventorySkinsListConfig = input<SkinsListConfig | null>(null);
  isProcessing = input<boolean>(false);

  theme = input<'withdraw-store' | 'default'>('default');

  readonly search = signal<string>('');
  readonly pure = model<boolean>(false);

  readonly sortOptions: SortOption[] = [
    {
      label: 'Highest Price',
      key: 'highest_price',
      fn: () => {
        this.sort.set(this.sortOptions[0]);
      },
    },
    {
      label: 'Lowest Price',
      key: 'lowest_price',
      fn: () => {
        this.sort.set(this.sortOptions[1]);
      },
    },
  ];
  readonly sort = signal<SortOption>(this.sortOptions[0]);
  readonly selectedSkins = model<Skin[]>([]);

  selectedSkinsPrice = computed(() => {
    return this.selectedSkins().reduce(
      (acc, item) => acc + Number(item.price),
      0
    );
  });

  action = output<any>();

  unavailableSkinsAssetIds = computed<string[]>(() => {
    const req = this.requirementsOptions();
    if (!req) return [];

    const maxItems = req.requirements_options_max_items;
    const maxValue = req.requirements_options_max_value;

    if (maxValue === undefined || maxValue === null) return [];

    const toCents = (v: number) => Math.round(Number(v) * 100);
    const selectedIds = new Set(this.selectedSkins().map((s) => s.assetid));
    const selectedCents = this.selectedSkins()
      .map((s) => toCents(s.price))
      .reduce((a, b) => a + b, 0);

    // If max items already reached - all unselected become unavailable
    if (maxItems !== undefined && this.selectedSkins().length >= maxItems) {
      return this.skins()
        .filter((s) => !selectedIds.has(s.assetid))
        .map((s) => s.assetid);
    }

    const remaining = Math.max(0, toCents(maxValue) - selectedCents);
    if (remaining <= 0) {
      return this.skins()
        .filter((s) => !selectedIds.has(s.assetid))
        .map((s) => s.assetid);
    }

    return this.skins()
      .filter(
        (s) => !selectedIds.has(s.assetid) && toCents(s.price) > remaining
      )
      .map((s) => s.assetid);
  });

  inventoryPrice = computed(() => {
    if (this.skinsResource.error()) return 0;
    return (
      this.skinsResource
        .value()
        ?.filter((skin) => this.filterByPriceRequirements(skin))
        ?.reduce((acc, item) => acc + Number(item.price), 0) ?? 0
    );
  });

  skinsResource = resource({
    params: () => {
      const endpoint = this.pure()
        ? this.inventoryPureOptions()?.items_pure_load_endpoint ??
          this.inventoryOptions().items_load_endpoint
        : this.inventoryOptions().items_load_endpoint;

      const options = this.pure()
        ? this.inventoryPureOptions()?.items_pure_load_endpoint_options ??
          this.inventoryOptions().items_load_endpoint_options
        : this.inventoryOptions().items_load_endpoint_options;
      return { endpoint, options };
    },
    loader: async (params) => {
      try {
        // Wait for the data: large inventories (3k+ items) take a while and the
        // BE acks its own errors. Rejects at once on disconnect; the safety net
        // only fires if the request was silently dropped.
        const response: CommonSkin[] = await this.socketService.emitUntilData(
          params.params.endpoint,
          params.params.options,
          {
            safetyTimeoutMs: SOCKET_TIMEOUT_INVENTORY_SAFETY_MS,
            abortSignal: params.abortSignal,
          }
        );
        return Array.isArray(response) ? response.filter((i) => Boolean(!!i)) : [];
      } catch (err) {
        throw err instanceof Error ? err : new Error(String(err));
      }
    },
  });

  /** True once a load has been pending for a while; drives the "large inventory" hint. */
  readonly isSlowLoad = signal(false);
  private static readonly SLOW_LOAD_HINT_DELAY_MS = 10_000;

  skins = computed<Skin[]>(() => {
    if (this.skinsResource.error()) return [];
    return (
      this.skinsResource
        .value()
        ?.map((skin) => {
          const skinObject: Skin = {
            ...skin,
            name: skin?.market_hash_name ?? skin?.name ?? '',
            image: normalizeSkinImage(skin.image),
            color: skin.color ?? skin.name_color ?? 'var(--gray)',
          };
          return skinObject;
        })
        .filter((skin) => {
          return this.filterByPriceRequirements(skin);
        })
        .filter((skin) => {
          const filteredBySearch = skin.name
            ?.toLowerCase()
            .includes(this.search().toLowerCase() ?? '');

          const isPureModeWithoutEndpoint =
            this.pure() &&
            !this.inventoryPureOptions()?.items_pure_load_endpoint;

          return isPureModeWithoutEndpoint
            ? this.sessionService.pureSkins().includes(skin?.name) &&
                filteredBySearch
            : filteredBySearch;
        })
        .sort((a, b) => {
          if (this.sort().key === 'highest_price') {
            return b.price - a.price;
          }
          return a.price - b.price;
        }) ?? []
    );
  });

  isShowRefreshAnimation = signal(false);
  readonly hasLoadedOnce = signal(false);

  isDisabledByRequirements = computed(() => {
    const isRequirements = !!this.requirementsOptions();
    if (!isRequirements) {
      return false;
    }

    return (
      this.selectedSkinsPrice() <
        (this.requirementsOptions()?.requirements_options_min_value ?? 0) ||
      this.selectedSkinsPrice() >
        (this.requirementsOptions()?.requirements_options_max_value ?? 0) ||
      this.selectedSkins().length >
        (this.requirementsOptions()?.requirements_options_max_items ?? 0) ||
      this.selectedSkins().length <
        (this.requirementsOptions()?.requirements_options_min_items ?? 0)
    );
  });

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public sessionService: SessionService,
    public depositService: DepositService,
    public socketService: SocketService
  ) {
    effect(() => {
      if (!this.skinsResource.error() && this.skinsResource.value() !== undefined) {
        this.hasLoadedOnce.set(true);
      }
    });

    // Show a hint if a load (first, refresh or pure/skins switch) is still
    // pending after a while.
    effect((onCleanup) => {
      if (!this.skinsResource.isLoading()) {
        this.isSlowLoad.set(false);
        return;
      }
      const timer = setTimeout(
        () => this.isSlowLoad.set(true),
        InventoryTemplateComponent.SLOW_LOAD_HINT_DELAY_MS
      );
      onCleanup(() => clearTimeout(timer));
    });

    // Self-heal: a disconnect rejects the pending load at once (see
    // SocketService.emitUntilData); once the socket is back, retry without a
    // click. Only from the error state, so a loaded list and the user's
    // selection survive reconnects.
    fromEvent(this.socketService.socket, 'connect')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.skinsResource.error()) this.skinsResource.reload();
      });
  }

  public async refreshInventory() {
    this.search.set('');
    this.isShowRefreshAnimation.set(true);
    this.skinsResource.reload();
    this.selectedSkins.set([]);
    await sleep(500);
    this.isShowRefreshAnimation.set(false);
  }

  handleSelectWithdrawStore(type: BalanceType) {
    if (type === 1 || type === 'pure') {
      this.pure.set(true);
    } else {
      this.pure.set(false);
    }
    this.selectedSkins.set([]);
  }

  private filterByPriceRequirements(skin: Skin) {
    const filters = this.requirementsOptions();
    const isFilterByPrice = !!filters;

    if (isFilterByPrice) {
      return skin.price <= filters.requirements_options_max_value;
    }
    return true;
  }

  toggleSortOptions() {
    this.sort().key === 'highest_price'
      ? this.sort.set(this.sortOptions[1])
      : this.sort.set(this.sortOptions[0]);
  }
}
